/**
 * Authentication utility functions
 */

/**
 * Get authentication token from sessionStorage or cookie
 * @returns {string|null} The authentication token or null if not found
 */
export const getAuthToken = () => {
  // Try cookie first (as per existing pattern in the codebase)
  if (typeof document !== 'undefined') {
    const cookieToken = document.cookie
      .split('; ')
      .find((row) => row.startsWith('kayease_token='))
      ?.split('=')[1];
    if (cookieToken) return cookieToken;
  }

  // Fallback to sessionStorage
  if (typeof window !== 'undefined') {
    try {
      return sessionStorage.getItem('authToken');
    } catch {
      return null;
    }
  }

  return null;
};

/**
 * Decode the JWT payload (base64url) WITHOUT verifying the signature.
 * For read-only UI hints only — never a security boundary. The server
 * enforces everything; this just lets the UI reflect the session.
 * @returns {object|null}
 */
export const decodeToken = () => {
  const token = getAuthToken();
  if (!token || typeof token !== 'string') return null;
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json =
      typeof atob !== 'undefined'
        ? atob(padded)
        : Buffer.from(padded, 'base64').toString('utf8');
    return JSON.parse(json);
  } catch {
    return null;
  }
};

/**
 * True when the current session is read-only — an inactive account that
 * may browse but not make changes. The backend blocks every write for
 * such a session (403 `VIEW_ONLY`); this mirror lets the UI show a
 * banner and fail writes gracefully.
 * @returns {boolean}
 */
export const isViewOnly = () => decodeToken()?.viewOnly === true;

/**
 * Why the user was sent back to the login form, when it was not their
 * own doing — e.g. an admin changed their account and the server ended
 * the session. Read once by the sign-in page, then cleared.
 */
export const takeSignOutReason = () => {
  if (typeof window === 'undefined') return '';
  try {
    const reason = sessionStorage.getItem('kaympus:signOutReason') || '';
    if (reason) sessionStorage.removeItem('kaympus:signOutReason');
    return reason;
  } catch {
    return '';
  }
};

/** The school portal's login form. */
export const SCHOOL_SIGN_IN_PATH = '/auth/sign-in';
/** The SuperAdmin's own login form — a different form on a different route. */
export const SUPERADMIN_SIGN_IN_PATH = '/auth/superadmin-login';

/**
 * Where a session of this role should be sent when it ends.
 *
 * SuperAdmins sign in at their own form, so dropping them on the school
 * form after logout (or after a session expiry) is a dead end — it asks
 * for a school code they don't have. Everyone else belongs on the school
 * form.
 */
export const signInPathForRole = (role) =>
  role === 'superadmin' ? SUPERADMIN_SIGN_IN_PATH : SCHOOL_SIGN_IN_PATH;

/** The signed-in role, read before any teardown clears it. */
export const getStoredRole = () => {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem('userRole');
  } catch {
    return null;
  }
};

/**
 * Logout — clears all authentication data and returns the user to the
 * login form they actually came from.
 * @param {Function} router - Next.js router instance
 * @param {Function} showToast - Toast notification function (optional)
 */
export const logout = (router, showToast = null) => {
  // Read the role BEFORE the teardown below wipes it — it decides which
  // login form we land on.
  const target = signInPathForRole(getStoredRole());

  // Capture the JWT before teardown and hand it to the push unregister —
  // the fetch inside runs after the cookie below is already gone. Fire
  // and forget: a failed unregister self-heals server-side once FCM
  // reports the locally-deleted token dead.
  const authTokenForPush = getAuthToken();
  import('@/lib/webPush')
    .then((m) => m.disableWebPush(authTokenForPush))
    .catch(() => {});

  // Clear sessionStorage
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('userRole');
    sessionStorage.removeItem('schoolCode');
    sessionStorage.removeItem('schoolInfo');
    sessionStorage.removeItem('phoneNumber');
    sessionStorage.removeItem('otpContext');
    // Personal data must not outlive the session on a shared computer:
    // the register-school draft carries a name, email, phones and address.
    try {
      localStorage.removeItem('kayease_school_onboarding');
      localStorage.removeItem('kayease_school_onboarding_key');
    } catch { /* private mode */ }
  }

  // Drop the in-memory permission caches so the next account signing in
  // on this tab never inherits this one's sidebar / gates. Lazy imports
  // keep utils/auth free of a hook dependency cycle.
  Promise.all([
    import('@/hooks/usePermissions').then((m) => m.invalidatePermissions?.()),
    import('@/hooks/useSuperAdminPermissions').then((m) => m.invalidateSAPermissions?.()),
  ]).catch(() => {});

  // Clear cookie
  if (typeof document !== 'undefined') {
    document.cookie = 'kayease_token=; Path=/; Max-Age=0; SameSite=Strict';
  }

  // Show logout toast if provided
  if (showToast) {
    showToast({
      message: 'You have been logged out successfully.',
      severity: 'success',
      debug: { context: 'logout' },
    });
  }

  // Redirect using replace to prevent back button navigation
  if (router) {
    router.replace(target);
  } else if (typeof window !== 'undefined') {
    // Use replace instead of href to prevent back button navigation
    window.location.replace(target);
  }
};

